import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DoorOpen, Loader2, ChevronRight } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { SendKeySmsFlow } from '@/components/send-key/SendKeySmsFlow';
import { useDevices } from '@/hooks/useDevices';
import { useDeviceStore } from '@/stores/deviceStore';
import { groupByFacility } from '@/lib/facilities';
import type { DeviceAssignment } from '@/types';

/**
 * SendKeyPage — "Tilldela nyckel". Nås från nyckelpanelen (DevicePanel) med
 * förvald enhet via ?device=<id>; utan förval visas enhetslistan. Ligger
 * inte i bottenmenyn.
 */
export default function SendKeyPage() {
  const { isLoading } = useDevices();
  const { assignments } = useDeviceStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAssignment, setSelectedAssignment] = useState<DeviceAssignment | null>(null);

  // Only show devices where user can generate (send) keys
  const validAssignments = assignments.filter(
    (a) => a.device?.device_code && a.can_generate_keys
  );

  const facilityGroups = useMemo(() => groupByFacility(validAssignments), [validAssignments]);

  // Effektivt val: manuellt val > förvald enhet (?device=) > enda enheten.
  const preselectedId = searchParams.get('device');
  const preselected = preselectedId
    ? validAssignments.find((a) => a.device_id === preselectedId) ?? null
    : null;
  const active =
    selectedAssignment ??
    preselected ??
    (validAssignments.length === 1 ? validAssignments[0] : null);

  const handleBack = () => {
    setSelectedAssignment(null);
    if (preselectedId) setSearchParams({}, { replace: true });
  };

  if (active?.device) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <SendKeySmsFlow
          deviceId={active.device.id}
          organizationId={active.organization_id}
          deviceName={active.device.name}
          onBack={handleBack}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-6 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Tilldela nyckel</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Välj en enhet för att tilldela en nyckel via SMS
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : validAssignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-lg font-medium">Inga enheter</p>
            <p className="text-sm text-muted-foreground mt-1">
              Du har inga enheter där du får skicka nycklar
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {facilityGroups.map((group) => (
              <section key={group.name} className="space-y-3">
                <h3 className="text-base font-semibold">{group.name}</h3>
                <div className="space-y-2">
                  {group.assignments.map((assignment) => (
                    <button
                      key={assignment.id}
                      onClick={() => setSelectedAssignment(assignment)}
                      className="w-full flex items-center gap-3 p-4 rounded-2xl glass-thin transition-all hover:scale-[1.01] active:scale-[0.98] text-left"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))] shrink-0">
                        <DoorOpen className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{assignment.device?.name}</p>
                        {assignment.device?.location && (
                          <p className="text-xs text-muted-foreground truncate">
                            {assignment.device.location}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
